<template>
  <div
    class="discussion-question"
    aria-label="discussion board question"
    :class="{ darkModeStyle: interactionMeta.darkMode }">
    <div class="discussion-wrapper">
      <div>
        <v-layout row py-2 class="pb-3">
          <v-icon class="py-1 pr-2" :dark="interactionMeta.darkMode">search</v-icon>
          <v-tooltip
            v-if="(
              userHasNotSubmittedDiscussionComment
              && isStudent
            )"
            v-model="visible"
            content-class="hidden-discussion-tooltip-alert"
            top>
            <template v-slot:activator="{ on }">
              <v-text-field
                v-on="on"
                :dark="interactionMeta.darkMode"
                class="discussion-search pa-0 ma-0"
                name="text-search"
                label="Search Discussion"
                aria-label="Search Discussion"
                clearable
                v-model="searchText"
                ref="discussionSearch"
                hide-details
                tabindex="0"
                :disabled="isLiveSession && interactionMeta.pastSessionTime"
                >
              </v-text-field>
            </template>
            <span>In order to view the discussion comments, submit one of your own.</span>
          </v-tooltip>
          <v-text-field
            v-else
            :dark="interactionMeta.darkMode"
            class="discussion-search pa-0 ma-0"
            name="text-search"
            label="Search Discussion"
            aria-label="Search Discussion"
            clearable
            v-model="searchText"
            ref="discussionSearch"
            hide-details
            tabindex="0"
            :disabled="isLiveSession && interactionMeta.pastSessionTime"
            >
          </v-text-field>
          <v-btn
            v-if="isInstructor"
            :size="14"
            flat
            small
            icon
            class="mx-0 my-1"
            @click="downloadDiscussions"
            label="download dicussions"
            aria-label="Download Discussions as Text File"
            :color="interactionMeta.darkMode ? '' : 'grey darken-1'"
            :dark="interactionMeta.darkMode"
            :disabled="isLiveSession && interactionMeta.pastSessionTime">
            <v-icon>file_download</v-icon>
          </v-btn>
          <v-icon
            :dark="interactionMeta.darkMode"
            :color="selectedFilterOptionId ? 'primary' : 'inactive'"
            class="pa-1">
              filter_list
          </v-icon>
          <v-flex xs6>
            <b-form-select
              aria-label="Filter Comments by Commenter"
              box
              class="full-width full-height transparentBackground"
              v-model="selectedFilterOptionId"
              :options="filterOptions"
              :class="{ darkModeSelectStyle: interactionMeta.darkMode }"
              :disabled="isLiveSession && interactionMeta.pastSessionTime"
              >
            </b-form-select>
          </v-flex>
        </v-layout>
        <v-layout column class="pb-3">
          <v-flex>
            <div
              :key="discussionInteractionKey"
              class="pp-interaction-title"
              role="heading"
              :aria-label="discussionHeader"
              xs12
              v-html="interaction.interactionText">
            </div>
          </v-flex>
          <interaction-attachments :attachments="interaction.interactionAttachments" />
        </v-layout>
      </div>
      <!-- end discussion-question header -->
      <div
        class="discussion-comments-wrapper"
        :id="discussionKey + '-comments'"
      >
        <template v-if="!userHasNotSubmittedDiscussionComment"> <!-- confusing, I know -->
          <template v-for="comment in filteredComments">
            <discussion-comment
              :key="comment.id"
              :comment="comment"
              :comments="comments"
              :discussionPreview="discussionPreview"
              :filtered="searchText || selectedFilterOptionId"
              :interactionId="interactionId"
              :expandAllReplyThreads="expandAllReplyThreads"
              @expand-all-reply-threads="handleExpandAllReplyThreads"
              :visible="visible"
            >
            </discussion-comment>
          </template>
        </template>
        <!-- end root comment wrapper -->
        <div class="loading-screen" v-if="interactionMeta.isProcessingData">
          <v-progress-circular indeterminate v-bind:size="50" class="branding-active-text"></v-progress-circular>
        </div>
        <!-- end loading spinner -->
      </div>
      <!-- end discussion wrapper -->
    </div>
    <discussion-input
      ref="newComment"
      :interactionId="interactionId"
      mode="comment"
      :showReplies="showReplies"
      @expand-all-reply-threads="handleExpandAllReplyThreads"
      :placeholder="newCommentPlaceholder"
      ariaLabel="Input field for new comment text.">
    </discussion-input>
  </div>
  <!-- end dicussion question -->
</template>

<script>
/* global _ */
/* global Blob */

// Components:
import InteractionAttachments from '@/components/interaction/InteractionAttachments';
import DiscussionComment from '@/components/interaction/discussion/DiscussionComment';
import DiscussionInput from '@/components/interaction/discussion/DiscussionInput';

// Plugins / Libraries:
import moment from 'moment';
import {
  mapGetters,
  mapMutations,
  mapActions,
  mapState
} from 'vuex';
import FileSaver from 'file-saver';

export default {
  name: 'interaction-discussion',
  props: [
    'interactionId',
    'visible'
  ],
  components: {
    DiscussionComment,
    DiscussionInput,
    InteractionAttachments,
  },
  data() {
    return {
      commentsLoaded: false,
      closestComment: {},
      dateTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
      discussionInteractionKey: 0,
      selectedFilterOptionId: null,
      inAutoScrollRange: false,
      searchText: null,
      showSearch: false,
      previousAutoScrollTime: null,
      expandAllReplyThreads: true,
      showReplies: true,
      userHasNotSubmittedDiscussionComment: false,
      filterOptions: [],
    };
  },
  computed: {
    ...mapState('player', [
      'formatedDuration',
      'isLiveSession',
      'playbackModeType',
      'setFirstFocus',
    ]),
    ...mapState('interactions', [
      'discussionCommentsShowReplies',
    ]),
    ...mapGetters('player', [
      'bulb',
      'bulbAttempt',
      'branding',
      'displayTime',
      'maxTime',
      'mobile',
      'playerState',
      'queryParams',
      'schoolId',
      'user',
      'userAgent',
    ]),
    ...mapGetters('interactions', [
      'discussionComments',
      'userSubmittedDiscussionComment',
    ]),
    isInstructor() {
      return this.user.userType === 'teacher';
    },
    isStudent() {
      return this?.user?.userType === 'student';
    },
    comments() {
      /**
       * Comment display logic works as follow:
       *
       * 1) Learners attempting bulb should not see all submitted discussion comments until they reach the timestamp in
       * the bulb that the discussion comment appears.
       *
       * 2) Instructors previewing a bulb with a section_id should see all discussion comments at the discussion forum's
       * display_in_time.
       */
      if (
        this.isStudent
        && this.hidesDiscussionCommentsUntilAnswered
        && !this.userSubmittedDiscussionComment(this.interaction.id, this.user.id)
      ) {
        this.userHasNotSubmittedDiscussionComment = true;
      } else {
        this.userHasNotSubmittedDiscussionComment = false;
      }
      let commentsArray = [];
      if (this.queryParams.get('type') === 'share' && this.bulbAttempt.sectionId) {
        commentsArray =
          _.filter(this.discussionComments(this.interaction.id), (c) => c.displayTime <= this.maxTime + 0.1) || [];
      } else {
        commentsArray =
          _.filter(this.discussionComments(this.interaction.id), (c) => c.displayTime <= this.displayTime + 0.1) || [];
      }
      return this.bulbAttempt.complete ? this.discussionComments(this.interaction.id) || [] : commentsArray;
    },
    commenters() {
      const users = _.sortBy(
        _.uniqBy(
          _.without(
            _.map(_.flatMapDeep(this.flatComments), (c) => {
              if (c.userId > 0)
                return {
                  value: c.userId,
                  text: `${c.lastName}, ${c.firstName}`,
                };
            }),
            undefined
          ),
          'value'
        ),
        'text'
      );
      return users;
    },
    commentTimes() {
      return this.comments.map((c) => {
        return { time: c.displayTime, id: c.id };
      });
    },
    discussionKey() {
      return `discussion-${this.interactionMeta.overlay ? 'h' : 's'}-${this.interaction.id}`;
    },
    discussionPreview() {
      // If anonymous user or teacher without a section use the preview discussions
      // comments are not saved and other users' comments cannot be viewed.
      return (
        this.user.userType === 'anonymous'
        || (
          this.user.userType === 'teacher'
          && !this.bulbAttempt.sectionId
        )
      );
    },
    filteredComments() {
      if (
        !this.selectedFilterOptionId
        && !this.searchText
      ) {
        return this.comments;
      }

      let filteredComments = this.flatComments;
      filteredComments = this.filterCommentsBasedOnSelectedFilterOption(filteredComments);
      if (this.searchText) {
        return filteredComments.filter((comment) => {
          let regex = new RegExp('(' + this.searchText + ')', 'i');
          return comment.text.match(regex);
        });
      }
      return filteredComments;
    },
    flatComments() {
      let flatComments = [];
      this.comments.forEach((c) => {
        c.replies.forEach((r) => {
          if (!r.deleted) flatComments.push(r);
        });
        if (!c.deleted) flatComments.push(c);
      });
      return flatComments;
    },
    // ! Due to naming convention overlap, can't refactor these computed properties to map them as Vuex getters
    // so we have to use the $store this way (until we truly refactor this file)
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    isDiscussionVisible() {
      return this.user.userType === 'student';
    },
    newCommentPlaceholder() {
      return `Posting as ${this.user.firstName} ${this.user.lastName}`;
    },
    discussionHeader() {
      return `Discussion interaction: ${this.interaction.interactionText.replace(/(<([^>]+)>)/gi, '')}`;
    },
    hidesDiscussionCommentsUntilAnswered() {
      return _.get(this, 'interaction.config.hide_discussion_posts_until_learner_contributes', false);
    },
    generateDiscussionFilterOptions() {
      return [
        {
          value: null, text: 'Filter option'
        },
        {
          label: 'Ordering',
          options: [
            {
              text: 'Newest',
              value: 1,
            },
            {
              text: 'Oldest',
              value: 1337,
            },
          ]
        },
        {
          label: 'Students',
          options: this.commenters,
        }
      ];
    },
  },
  methods: {
    ...mapMutations('player', [
      'setSnackbar',
    ]),
    ...mapMutations('interactions', [
      'setDiscussionCommentsShowReplies',
    ]),
    ...mapActions('interactions', [
      'loadDiscussionComments',
      'loadDummyDiscussionComments',
    ]),
    autoScroll() {
      this.previousAutoScrollTime = this.displayTime;
      const newClosestComment = this.commentTimes.sort(
        (a, b) => Math.abs(this.previousAutoScrollTime - a.time) - Math.abs(this.previousAutoScrollTime - b.time)
      )[0];
      if (newClosestComment && newClosestComment.id !== this.closestComment.id) {
        this.closestComment = newClosestComment;
        document.getElementById(`comment-${this.closestComment.id}`).scrollIntoView({ behavior: 'smooth' });
      }
    },
    checkForDiscussionCompletion() {
      let hasCompleted = this.interaction.isRequired && this.interactionMeta.isInteractionAnswered;
      if ((this.user.userType !== 'student' && this.playbackModeType !== 'learner') || this.user.readOnly)
        hasCompleted = true;
      if (!hasCompleted) {
        // Checks to make sure discussionAttempts were not from an archived interactionAttempt
        const archivedDate = new Date('2100-12-01T00:00:00.000Z');
        for (var c of this.comments) {
          if (c.self && new Date(c.interactionAttemptArchived) >= archivedDate) {
            hasCompleted = true;
            break;
          }
          for (var r of c.replies) {
            if (r.self && new Date(r.interactionAttemptArchived) >= archivedDate) {
              hasCompleted = true;
              break;
            }
          }
        }
        if (hasCompleted) {
          this.interactionMeta.isInteractionAnswered = true;
        }
      } else {
        this.interactionMeta.isInteractionAnswered = true;
      }
    },
    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (this.showSearch) setTimeout(() => { this.$refs.discussionSearch.focus(); }, 401);
    },
    fromNow(time) {
      return moment(time).fromNow();
    },
    downloadDiscussions() {
      let html = '';
      this.comments.forEach((c, i) => {
        c.text = c.text.replace(/\<(?!img|audio).*?\>\%09%u2022/g, '');
        if (c.text.match(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g)) {
          c.text = c.text.replace(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g, '$3');
          c.text = c.text.replace(/\↵*/g, '').split('\n');
          c.text = c.text.slice(1, c.text.length - 1);
          html +=
            c.name +
            ` - ${this.fromNow(c.created)}` +
            '\r\n' +
            `(${this.formatedDuration(c.displayTime)}) - | ` +
            c.text.join('\r\n          |\r\n          | ') +
            '\r\n\r\n';
        } else {
          html +=
            c.name +
            ` - ${this.fromNow(c.created)}` +
            '\r\n' +
            `(${this.formatedDuration(c.displayTime)}) - ` +
            c.text +
            '\r\n';
          html += c.replies.length ? '\\' : '\r\n';
        }
        if (c.replies.length > 0) {
          c.replies.forEach((r, i) => {
            r.text = r.text.replace(/\<(?!img|audio).*?\>\%09%u2022/g, '');
            if (r.text.match(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g)) {
              r.text = r.text.replace(/((<ul>|<ol>)|<li>(.*)<\/li>|(<\/ul>|<\/ol>))/g, '$3');
              r.text = r.text.replace(/\↵*/g, '').split('\n');
              r.text = r.text.slice(1, r.text.length - 1);
              html +=
                '\r\n  |--' +
                r.name +
                ` - ${this.fromNow(r.created)}` +
                '\r\n' +
                '  |  ' +
                `(${this.formatedDuration(r.displayTime)}) - | ` +
                r.text.join('\r\n  |            |\r\n  |            | ');
              html += c.replies.length - 1 === i ? '\r\n\r\n' : '\r\n  |\r\n  |';
            } else {
              html +=
                '\r\n  |--' +
                r.name +
                ` - ${this.fromNow(r.created)}` +
                '\r\n' +
                '  |  ' +
                `(${this.formatedDuration(r.displayTime)}) - ` +
                r.text;
              html += c.replies.length - 1 === i ? '\r\n\r\n' : '\r\n' + '  |' + '\r\n' + '  |';
            }
          });
        }
      });
      const blob = new Blob([html], { type: 'text/plain;charset=utf-8' });
      FileSaver.saveAs(blob, `playposit-discussions-${this.interactionId}.txt`);
    },
    handleExpandAllReplyThreads(val) {
      this.expandAllReplyThreads = val;
      this.showReplies = val;
      this.comments.filter( (comment) => comment.replies.length > 0).forEach( comment => {
        const discussionCommentReply = {
          commentId: comment.id,
          showReplies: val,
        };
        this.setDiscussionCommentsShowReplies(discussionCommentReply);
      });
    },
    filterCommentsBasedOnSelectedFilterOption(filteredComments) {
      if (this.selectedFilterOptionId) { // meaning the user has selected some filter option
        // Newest filter:
        if (this.selectedFilterOptionId === 1) {
          return _.orderBy(_.flatMapDeep(filteredComments), 'created', 'desc');
        }
        // Oldest filter:
        if (this.selectedFilterOptionId === 1337) {
          return _.orderBy(_.flatMapDeep(filteredComments), 'created', 'asc');;
        }
        // Otherwise, the user is filtering on the user names that have made comments:
        return _.filter(_.flatMapDeep(filteredComments), (c) => c.userId === this.selectedFilterOptionId);
      }
    },
  },
  watch: {
    comments: {
      handler() {
        this.checkForDiscussionCompletion();
      },
      deep: true,
    },
    displayTime(n, o) {
      const comments = document.querySelector(`#interaction-${this.interaction.id}`);
      if (!comments) return;
      const mouseOver = comments.parentElement.querySelector(':hover') === comments;
      if (this.previousAutoScrollTime === null || this.previousAutoScrollTime === undefined) {
        this.previousAutoScrollTime = n - 5;
      }
      if (this.visible && !mouseOver && n !== o && Math.abs(n - this.previousAutoScrollTime) >= 1) {
        this.autoScroll();
      }
    },
    visible(newValue, oldValue) {
      if (this.visible && this.interaction.interactionText.includes('h5p.com')) {
        this.discussionInteractionKey += 1;
      }
      if (!newValue && oldValue) this.discussionInteractionKey += 1;
    },
    commenters(newValue, oldValue) {
      if (newValue) {
        this.filterOptions = this.generateDiscussionFilterOptions;
      }
    },
  },
  async mounted() {
    await this.$nextTick();
    if (this.discussionPreview) {
      this.loadDummyDiscussionComments({ interactionId: this.interactionId });
      return;
    }
    await this.loadDiscussionComments({
      sectionId: this.bulbAttempt.sectionId,
      interactionId: this.interaction.id,
    });
    this.checkForDiscussionCompletion();
  },
};
</script>

<style>
/* Materialize CSS overwrites */
.question-app .q-discussion {
  overflow-y: initial !important;
}
.q-discussion .container {
  max-width: 100% !important;
  margin: 0 !important;
  width: 100% !important;
}
#EqnEditor .toolbar {
  box-shadow: none !important;
}
.q-discussion .container .row {
  width: 100% !important;
}
.q-discussion h3.comment-name {
  font-size: 14px;
  line-height: 1.1;
  margin: 0;
  font-weight: 700;
}

.discussion-wrapper {
  overflow: auto;
  position: relative;
  flex-grow: 100;
}

.q-discussion .btn.round {
  display: flex;
  border-radius: 50%;
  height: 56px;
  width: 56px;
  padding: 0;
}
.q-discussion .btn.btn--small {
  height: 28px;
  width: 28px;
}
/* */
.discussion-comments-wrapper {
  flex-grow: 1;
  overflow: auto;
  /* for Firefox */
  min-height: 0;
}

.discussion-comments-wrapper.teacher-notes {
  height: 95%;
}

.discussion-comments {
  height: 100%;
  position: relative;
}

.discussion-comments .list {
  background: transparent;
}

.discussion-comments .notes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notes-search label {
  padding: 18px 0 0;
  margin-top: -35px;
}

.discussion-comments-wrapper .more-messages-below {
  position: absolute;
  bottom: 0;
  z-index: 1000;
  width: 100%;
  text-align: center;
  padding: 0.5rem 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  user-select: none;
  cursor: pointer;
}

.loading-screen, .hidden-comments {
  z-index: 999999999;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background: transparent !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.discussion-comment:not(:last-of-type) {
  margin-bottom: 16px;
}

.discussion-comment .comment-content {
  word-wrap: break-word;
  white-space: normal;
}

.discussion-comment .comment-name > span {
  position: relative;
}

.comment-actions {
  margin-top: 8px;
  font-size: 12px;
}

.comment-actions .btn {
  min-width: 0;
}

.comment-actions span {
  user-select: none;
  text-transform: uppercase;
  font-weight: 500;
  margin-left: 3px;
}

.discussion-comment span.replies-button {
  user-select: none;
  font-weight: 500;
}

.comment-actions a:not(:last-of-type) {
  margin-right: 8px;
}

.comment-actions .v-btn .v-btn__content {
  padding: 0;
  transition: none;
}

.discussion-comment img {
  max-width: 100%;
}

.comment-timestamp:hover {
  font-weight: 700;
  cursor: pointer;
}

.comment-replies {
  margin-top: 8px;
}

.discussion-search .v-label {
  font-size: 12px;
}

.vue-avatar--wrapper span {
  height: 18px;
  line-height: 20px;
}

.darkModeStyle {
  color: white;
}

.darkModeSelectStyle {
  color: white !important;
}

.transparentBackground {
  background: transparent !important;
}

.hidden-discussion-tooltip-alert {
  left: 61px !important;
  background: #FF9800 !important; /* our player toast warning colour */
  color: #000000 !important;
  font-weight: 700 !important;
}
</style>
